import React, { useState, useEffect, useCallback } from 'react';
import { X, ClockCounterClockwise, PaperPlaneTilt, Trash } from '@phosphor-icons/react';
import API from '@/lib/api';
import { toast } from 'sonner';

/**
 * MailScheduledModal — beheer pending uitgestelde mails.
 *
 * Toont alle pending geplande mails met:
 *  - Onderwerp + naar wie
 *  - Wanneer ze verstuurd worden
 *  - Knop: ✉️ Nu verzenden (forceer)
 *  - Knop: 🗑️ Annuleer
 */
export default function MailScheduledModal({ onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get(`/mail/scheduled?status=${statusFilter}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Kon geplande mails niet laden');
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const cancelSched = async (sid) => {
    if (!confirm('Geplande mail annuleren?')) return;
    try {
      await API.delete(`/mail/scheduled/${sid}`);
      toast.success('Geannuleerd');
      load();
      onChange?.();
    } catch (e) {
      toast.error('Annuleren mislukt');
    }
  };

  const sendNow = async (sid) => {
    if (!confirm('Mail nu direct verzenden in plaats van de geplande tijd?')) return;
    try {
      await API.post(`/mail/scheduled/${sid}/send-now`);
      toast.success('Mail verzonden');
      load();
      onChange?.();
    } catch (e) {
      toast.error('Verzenden mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const STATUS_LABELS = {
    pending: { label: 'In wacht', cls: 'bg-purple-100 text-purple-800' },
    sending: { label: 'Verzendt', cls: 'bg-blue-100 text-blue-800' },
    sent: { label: 'Verzonden', cls: 'bg-green-100 text-green-800' },
    failed: { label: 'Mislukt', cls: 'bg-red-100 text-red-800' },
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="mail-scheduled-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b bg-purple-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={20} weight="duotone" className="text-purple-700" />
            <div>
              <h3 className="text-base font-bold text-gray-900">Geplande mails</h3>
              <p className="text-xs text-gray-600 mt-0.5">Uitgestelde mails — worden automatisch verzonden op gepland moment</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-purple-100 rounded"><X size={18} /></button>
        </div>

        <div className="px-5 py-2 border-b flex items-center gap-2 flex-wrap">
          {['pending', 'sent', 'failed', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 text-xs rounded font-semibold ${statusFilter === s ? 'bg-purple-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              data-testid={`mail-scheduled-filter-${s}`}
            >
              {s === 'pending' ? 'In wacht' : s === 'sent' ? 'Verzonden' : s === 'failed' ? 'Mislukt' : 'Alle'}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {loading && <div className="text-center text-sm text-gray-500 py-8">Laden...</div>}
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <ClockCounterClockwise size={48} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Geen geplande mails in deze status.</p>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2">
              {items.map(it => {
                const dt = new Date(it.scheduled_at);
                const statusInfo = STATUS_LABELS[it.status] || { label: it.status, cls: 'bg-gray-100 text-gray-700' };
                return (
                  <div key={it.id} className="border border-gray-200 rounded p-3 hover:bg-gray-50" data-testid={`mail-scheduled-row-${it.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{it.preview_subject || '(geen onderwerp)'}</div>
                        <div className="text-xs text-gray-500 truncate">→ {(it.preview_to || []).join(', ')}</div>
                      </div>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${statusInfo.cls} flex-shrink-0`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      🕒 Gepland: <span className="font-semibold">{dt.toLocaleString('nl-BE')}</span>
                      {it.attempts > 0 && <span className="ml-2 text-amber-600">· {it.attempts} pogingen</span>}
                    </div>
                    {it.last_error && <div className="text-[11px] text-red-600 mt-1">⚠ {it.last_error}</div>}
                    {it.status === 'pending' && (
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => sendNow(it.id)}
                          className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded inline-flex items-center gap-1 font-semibold"
                          data-testid={`mail-scheduled-send-now-${it.id}`}
                        >
                          <PaperPlaneTilt size={12} weight="fill" /> Nu verzenden
                        </button>
                        <button
                          onClick={() => cancelSched(it.id)}
                          className="px-2 py-1 text-xs bg-white hover:bg-red-50 border border-red-300 text-red-700 rounded inline-flex items-center gap-1"
                          data-testid={`mail-scheduled-cancel-${it.id}`}
                        >
                          <Trash size={12} /> Annuleer
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
